import { faArrowRightFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthPageLayoutV2 from '../../components/Layout/AuthPageLayoutV2';
import useDeviceType from '../../hooks/useDeviceType';
import { useLogout } from '../../hooks/useLogout';
import useQueryParams from '../../hooks/useQueryParams';
import { IdentitySummaryResponseMfaTypeEnum } from '../../openapi/keymaker';
import { RootState } from '../../types';
import { getAuthCookie } from '../../utils/AuthUtils';
import { constructLink } from '../../utils/UrlUtils';
import MfaAppPinCard from './MfaCards/MfaAppPinCard';
import MfaCard, { MfaCardProps } from './MfaCards/MfaCard';
import MfaMethodSelectCard from './MfaCards/MfaMethodSelectCard';
import MfaPhoneNumberCard from './MfaCards/MfaPhoneNumberCard';
import MfaPinCard from './MfaCards/MfaPinCard';
import MfaSetupCard from './MfaCards/MfaSetupCard';

interface EnableMFARouteV2Props {}

interface Query {
  redirectTo?: string;
}

const EnableMFARouteV2: React.FC<EnableMFARouteV2Props> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { isMobile } = useDeviceType();
  const isAgentContext = location.pathname === '/agent-enable-mfa';
  const { redirectTo } = useQueryParams<Query>();
  const { keymakerCurrentUser } = useSelector((state: RootState) => state.auth);
  const finalUrl = redirectTo ? redirectTo : '/';
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationPhoneNumber, setVerificationPhoneNumber] = useState<
    string | undefined
  >(undefined);
  const [selectedMFAMethod, setSelectedMFAMethod] =
    useState<IdentitySummaryResponseMfaTypeEnum>();

  const TextMessageSteps = useMemo<MfaCardProps[]>(
    () => [
      {
        title: 'Text Messages (SMS)',
        children: (
          <MfaPhoneNumberCard
            onSendMfaSms={(phoneNumber) => {
              setVerificationPhoneNumber(phoneNumber);
              setCurrentStep((prev) => prev + 1);
            }}
            isAgentContext={isAgentContext}
            redirectTo={redirectTo}
          />
        ),
        buttonText: 'Back',
        onButtonClick: () => setCurrentStep((prev) => prev - 1),
      },
      {
        title: 'Text Messages (SMS)',
        children: (
          <MfaPinCard
            key={verificationPhoneNumber ?? 'no-phone'}
            verificationPhoneNumber={verificationPhoneNumber}
            onPinVerified={() => setCurrentStep((prev) => prev + 1)}
            isForceMfa
            isAgentContext={isAgentContext}
            redirectTo={redirectTo}
          />
        ),
        buttonText: 'Back',
        onButtonClick: () => setCurrentStep((prev) => prev - 1),
      },
    ],
    [isAgentContext, redirectTo, verificationPhoneNumber],
  );

  const handleMfaAppPinVerified = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const mfaAppPinCardChild = useMemo(
    () => (
      <MfaAppPinCard
        onPinVerified={handleMfaAppPinVerified}
        isForceMfa
        isAgentContext={isAgentContext}
        redirectTo={redirectTo}
      />
    ),
    [isAgentContext, redirectTo, handleMfaAppPinVerified],
  );

  const AuthenticatorAppSteps = useMemo<MfaCardProps[]>(
    () => [
      {
        title: 'Authenticator App',
        children: mfaAppPinCardChild,
        buttonText: 'Back',
        onButtonClick: () => setCurrentStep((prev) => prev - 1),
      },
    ],
    [mfaAppPinCardChild],
  );

  const initialSteps = useMemo<MfaCardProps[]>(
    () => [
      {
        withShieldIcon: true,
        title: 'Two-Factor Authentication',
        children: (
          <MfaSetupCard
            onSetupClick={() => setCurrentStep((prev) => prev + 1)}
          />
        ),
      },
      {
        withShieldIcon: true,
        title: 'Select a Two-Factor Authentication Method',
        children: (
          <MfaMethodSelectCard
            onContinue={(method) => {
              setSelectedMFAMethod(method);
              setCurrentStep((prev) => prev + 1);
            }}
          />
        ),
        buttonText: 'Cancel',
        onButtonClick: () => setCurrentStep((prev) => prev - 1),
      },
    ],
    [],
  );

  const steps = useMemo<MfaCardProps[]>(() => {
    if (!selectedMFAMethod) {
      return initialSteps;
    }

    const insertSteps =
      selectedMFAMethod === IdentitySummaryResponseMfaTypeEnum.Sms
        ? TextMessageSteps
        : AuthenticatorAppSteps;

    const updated = [...initialSteps];
    updated.splice(2, 0, ...insertSteps);
    return updated;
  }, [
    selectedMFAMethod,
    initialSteps,
    TextMessageSteps,
    AuthenticatorAppSteps,
  ]);

  useEffect(() => {
    if (!getAuthCookie()) {
      navigate(
        constructLink(isAgentContext ? '/agent-login' : '/login', {
          redirectTo,
        }),
      );
      return;
    }

    if (!!keymakerCurrentUser?.mfaType) {
      navigate(finalUrl, { replace: true });
    }
  }, [finalUrl, navigate, keymakerCurrentUser, redirectTo, isAgentContext]);

  return (
    <AuthPageLayoutV2
      rightHeader={
        isMobile ? (
          <FontAwesomeIcon
            size='sm'
            icon={faArrowRightFromBracket}
            className='text-white cursor-pointer'
            onClick={() => {
              logout();
              navigate(finalUrl);
            }}
          />
        ) : (
          <div
            className='flex py-1.5 px-3 justify-center items-center gap-1.5 rounded-[5px] border border-reskin-regent-400 bg-white cursor-pointer'
            onClick={() => {
              logout();
              navigate(finalUrl);
            }}
          >
            <FontAwesomeIcon
              size='sm'
              icon={faArrowRightFromBracket}
              className='text-reskin-primary-dark'
            />
            <div className='font-inter-medium text-sm leading-[18px]'>
              Logout
            </div>
          </div>
        )
      }
    >
      <div className='flex flex-col flex-grow justify-center items-center m-6 lg:mx-auto'>
        <MfaCard {...steps[currentStep]} />
      </div>
    </AuthPageLayoutV2>
  );
};

export default EnableMFARouteV2;
